import type { SessionRecorder, UrlAttributeScrubber } from '@dash0/sdk-web';
import type { ObservabilityUser } from './index';
import { redactUrl as redact } from './redact-url';

const DASH0_INGEST_URL = 'https://ingress.eu-west-1.aws.dash0.com';

/**
 * Public by construction (inlined in the bundle): an Ingesting-only token scoped to one dataset.
 * Unset means telemetry off.
 */
const AUTH_TOKEN = process.env.NEXT_PUBLIC_DASH0_WEB_TOKEN;

/** One deployment is one seller; this is the technical seller name, not the display name. */
const SELLER_ID = process.env.NEXT_PUBLIC_SELLER_ID;

const TRACE_PROPAGATION_URLS: RegExp[] = [
  /https:\/\/.*\.startcatalog\.com/,
  /https:\/\/.*\.catalogdemo\.fr/,
  /https:\/\/.*\.babymoov\.com/,
  /https:\/\/.*\.fimm\.fr/,
  /https:\/\/.*\.manufactureh\.com/,
  /https:\/\/.*\.cutbyfred\.com/,
  /https:\/\/.*\.nogent3etoiles\.com/,
  /https:\/\/.*\.gardette\.fr/,
];

const REPLAY_META_EVENT = 4;

const urlAttributeScrubber: UrlAttributeScrubber = (attr) => ({
  ...attr,
  'url.full': redact(attr['url.full']),
  'url.path': attr['url.path'] && redact(attr['url.path']),
  'url.query': attr['url.query'] && redact(attr['url.query']),
});

/**
 * rrweb's Meta event carries the raw page URL and the SDK forwards replay events untouched,
 * so `urlAttributeScrubber` never sees it.
 */
function scrubReplayUrls(recorder: SessionRecorder): SessionRecorder {
  return (options) =>
    recorder({
      ...options,
      emit: (event, isCheckout) => {
        const data = event.data as { href?: unknown } | undefined;
        options.emit(
          event.type === REPLAY_META_EVENT && typeof data?.href === 'string'
            ? { ...event, data: { ...data, href: redact(data.href) } }
            : event,
          isCheckout,
        );
      },
    });
}

let scrubberInstalled = false;

/**
 * Raw URLs reach the OTLP bodies through paths no SDK option covers (the page-load navigation
 * timing, failed XHR messages), so the bodies are redacted on the way out. The SDK keeps the
 * `fetch` it finds when its module is first evaluated: this must run before the first import.
 */
function installTelemetryScrubber(): void {
  if (scrubberInstalled || typeof window === 'undefined') return;
  scrubberInstalled = true;

  const nativeFetch = window.fetch;
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let url = '';
    try {
      url = input instanceof Request ? input.url : String(input);
    } catch {
      // Let the native fetch reject an input it cannot convert.
    }
    const body = init?.body;
    if (url.startsWith(DASH0_INGEST_URL) && typeof body === 'string') {
      return nativeFetch.call(window, input, { ...init, body: redact(body) });
    }
    return nativeFetch.call(window, input, init);
  };
}

function loadSdk() {
  installTelemetryScrubber();
  return import('@dash0/sdk-web');
}

let loaded = false;

/** Initialise the SDK once. Safe to call repeatedly, never throws. */
export async function loadDash0Rum(
  environment: string,
  version: string,
): Promise<void> {
  if (typeof window === 'undefined' || loaded || !AUTH_TOKEN) return;
  loaded = true;

  try {
    const sdk = await loadSdk();
    sdk.init({
      serviceName: 'store-front',
      serviceVersion: version,
      environment,
      // No `dataset`: the token pins it.
      endpoint: { url: DASH0_INGEST_URL, authToken: AUTH_TOKEN },
      additionalSignalAttributes: SELLER_ID
        ? { 'seller.name': SELLER_ID }
        : undefined,
      propagators: [{ type: 'traceparent', match: TRACE_PROPAGATION_URLS }],
      urlAttributeScrubber,
      // The body redaction above cannot read a compressed body.
      enableTransportCompression: false,
    });

    if (environment !== 'prod') return;

    // Dash0 replay defaults kept: typed input is masked. Started after `init`, a failed
    // recorder chunk must only cost the replay.
    await import('@dash0/sdk-web/session-recording')
      .then(({ recorder }) =>
        sdk.startSessionRecording(scrubReplayUrls(recorder)),
      )
      .catch(() => undefined);
  } catch {
    loaded = false;
  }
}

/** Attach the logged-in buyer to the session. */
export async function setDash0User(user: ObservabilityUser): Promise<void> {
  if (!AUTH_TOKEN) return;
  try {
    const { identify } = await loadSdk();
    identify(user.id, { name: user.name, email: user.email });
  } catch {
    // Fail soft.
  }
}
